import { Injectable, Injector } from '@angular/core';
import { RealtimeCursor } from '@common/api/realtime';
import { EntityInitializerService } from '@ep-om/project/service/entityInitializer.service';
import { AuthQuery } from '@ep-om/project/auth/auth.query';
import { AuthService } from '@ep-om/project/auth/auth.service';
import { LastUpdateQuery } from '@ep-om/project/state/lastUpdate/lastUpdate.query';
import { LastUpdateState } from '@ep-om/project/state/lastUpdate/lastUpdate.store';
import { ProjectQuery } from '@ep-om/project/state/project/project.query';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { RealtimeV2AdmissionService } from './realtime-v2-admission.service';
import { RealtimeV2EntitySyncService } from './realtime-v2-entity-sync.service';
import { RealtimeV2MessageDispatcherService } from './realtime-v2-message-dispatcher.service';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';
import { RealtimeEngine, RealtimeEngineService } from './realtime-engine.service';
import { shouldUseRealtimeV2 } from './realtime-v2-feature';
import { toRealtimeV2EntityName } from './realtime-v2-entity-name-map';
import { REALTIME_V2_GLOBAL_SCOPE_READY_TIMEOUT_MS } from './realtime-v2.models';

@Injectable({
  providedIn: 'root',
})
export class RealtimeV2RuntimeService {
  private runtimeEnabled = false;
  private runtimeSubscription = new Subscription();
  private enteredProjectId: string = null;
  private desiredProjectId: string = null;
  private projectScopeSyncing = false;
  private projectScopeSyncRequested = false;
  private entityStoresRegistered = false;

  constructor(
    private authQuery: AuthQuery,
    private authService: AuthService,
    private lastUpdateQuery: LastUpdateQuery,
    private projectQuery: ProjectQuery,
    private dispatcher: RealtimeV2MessageDispatcherService,
    private socket: RealtimeV2SocketService,
    private entitySync: RealtimeV2EntitySyncService,
    private engine: RealtimeEngineService,
    private injector: Injector,
    // Iniettato per istanziarlo al bootstrap del runtime: deve ascoltare gli eventi di ammissione
    // del socket fin da subito, prima di qualsiasi enterHome/enterProject.
    private admission: RealtimeV2AdmissionService,
  ) {
    this.dispatcher.start();

    // Il runtime v2 è attivo solo quando: motore = v2 (deciso dall'arbitro) E sessione loggata.
    // Al logout resetStores azzera il token: accessToken$ emette null -> disableRuntime ->
    // socket v2 disconnesso, niente messaggi residui (es. current_user_updated) che ripopolino
    // l'auth a sessione chiusa. Al login il token torna -> riabilitazione. Stesso gating del
    // socket legacy v1 in SocketIoService. Il motore (engine$) resta stabile per tutta la pagina.
    combineLatest([this.engine.engine$, this.authQuery.accessToken$]).pipe(
      map(([engine, token]) => engine === 'v2' && !!token),
      distinctUntilChanged(),
    ).subscribe((active: boolean) => this.startEngine(active));

    // Quando arriva l'utente allineo il motore (flag per-utente esplicito, oppure undefined =
    // default di istanza da settings.json, vedi shouldUseRealtimeV2). Due strade:
    // - fuori da un progetto (tipicamente al login, nessuna vista montata): switch IN-PLACE, niente
    //   reload -> l'auth appena settata non si perde (akita persistState potrebbe non aver flushato).
    //   È il caso che, partendo da logout (engine=v1) con utente v2, prima causava login-KO-poi-OK.
    // - dentro un progetto: reconcile() con reload, per un remount pulito senza far esplodere i
    //   template su resetStores. Il cooldown anti-rimbalzo vive nell'arbitro.
    this.authQuery.user$.pipe(
      filter((user) => !!user && !!user.settings),
      map((user): RealtimeEngine => (shouldUseRealtimeV2(user) ? 'v2' : 'v1')),
      distinctUntilChanged(),
    ).subscribe((desired) => {
      if (desired === this.engine.engine) {
        return;
      }
      if (this.projectQuery.getActiveId()) {
        this.engine.reconcile(desired);
      } else {
        this.engine.setEngine(desired);
      }
    });
  }

  private startEngine(enabled: boolean): void {
    if (enabled) {
      this.enableRuntime();
      return;
    }
    this.disableRuntime();
  }

  private enableRuntime(): void {
    if (this.runtimeEnabled) {
      return;
    }

    this.runtimeEnabled = true;
    this.runtimeSubscription = new Subscription();

    // Istanzia gli store Akita prima di connettere il socket: lo scope global stream-a
    // entita' (es. TypeCategory) gia' all'enterHome, prima che il project guard scatti.
    // Senza store registrato, runEntityStoreAction esplode su __stores__[name] undefined.
    this.ensureEntityStoresRegistered();

    this.runtimeSubscription.add(
      this.socket.connected$.pipe(
        filter((connected) => connected === true),
      ).subscribe(() => this.enterCurrentScopes()),
    );

    // Su disconnessione il socket riconnette come nuova connessione: il server perde
    // la membership delle room, quindi dobbiamo dimenticare lo scope progetto entrato
    // per forzarne il ri-ingresso al prossimo connected$.
    this.runtimeSubscription.add(
      this.socket.connected$.pipe(
        filter((connected) => connected === false),
      ).subscribe(() => this.resetEnteredProjectScope()),
    );

    this.runtimeSubscription.add(
      this.projectQuery.active$.pipe(
        map((project) => project?.id || null),
        distinctUntilChanged(),
      ).subscribe((projectId) => this.syncProjectScope(projectId as string)),
    );

    this.socket.connect();
  }

  // Risolve EntityInitializerService lazy: il suo costruttore inietta tutti i service
  // di stato, che a loro volta istanziano (e registrano in Akita) i rispettivi store.
  // Una volta sola: gli store restano registrati per tutta la vita dell'app.
  private ensureEntityStoresRegistered(): void {
    if (this.entityStoresRegistered) {
      return;
    }

    this.injector.get(EntityInitializerService);
    this.entityStoresRegistered = true;
  }

  private disableRuntime(): void {
    this.runtimeEnabled = false;
    this.runtimeSubscription.unsubscribe();
    this.runtimeSubscription = new Subscription();
    this.enteredProjectId = null;
    this.desiredProjectId = null;
    this.projectScopeSyncRequested = false;
    this.socket.disconnect();
  }

  private resetEnteredProjectScope(): void {
    this.enteredProjectId = null;
  }

  private async enterCurrentScopes(): Promise<void> {
    // Prima di costruire/inviare i cursor scartiamo quelli avanti rispetto ai dati persistiti
    // (vedi RealtimeV2EntitySyncService.reconcilePersistedCursors), cosi' evitiamo gap permanenti.
    this.entitySync.reconcilePersistedCursors();
    await this.enterHomeScope();
    await this.syncProjectScope(this.projectQuery.getActiveId() as string);
  }

  private async enterHomeScope(): Promise<void> {
    // Ogni (ri)enterHome apre un nuovo ciclo di sync globale: lo stato torna non-ready finche' il
    // backend non rimanda scope_ready. E' il reset autoritativo (anche su reconnect) che impedisce
    // a un enterProject di procedere sul ready della connessione precedente.
    this.entitySync.resetGlobalScopeReady();
    try {
      await this.socket.enterHome(this.buildGlobalCursors());
    } catch (error) {
      console.warn('[REALTIME-V2] enterHome failed', error);
    }
  }

  private async syncProjectScope(projectId: string): Promise<void> {
    this.desiredProjectId = projectId || null;
    await this.reconcileProjectScope();
  }

  private async reconcileProjectScope(): Promise<void> {
    if (this.projectScopeSyncing) {
      this.projectScopeSyncRequested = true;
      return;
    }

    this.projectScopeSyncing = true;
    try {
      do {
        this.projectScopeSyncRequested = false;
        await this.applyDesiredProjectScope();
      } while (this.projectScopeSyncRequested);
    } finally {
      this.projectScopeSyncing = false;
    }
  }

  private async applyDesiredProjectScope(): Promise<void> {
    if (!this.runtimeEnabled || !this.socket.connected$.value || this.enteredProjectId === this.desiredProjectId) {
      return;
    }

    const targetProjectId = this.desiredProjectId;
    const previousProjectId = this.enteredProjectId;
    this.enteredProjectId = null;

    if (previousProjectId) {
      try {
        await this.socket.leaveProject(previousProjectId);
      } catch (error) {
        console.warn('[REALTIME-V2] leaveProject failed', error);
      }
    }

    if (
      !this.runtimeEnabled
      || !this.socket.connected$.value
      || this.desiredProjectId !== targetProjectId
    ) {
      this.projectScopeSyncRequested = true;
      return;
    }

    if (!targetProjectId) {
      return;
    }

    // Gate: non si entra MAI in un progetto prima che lo scope globale della sessione sia ready
    // (le entita' di base, es. metainterfacce, devono essere gia' sincronizzate, altrimenti una
    // batch activity per uno store non ancora creato esploderebbe).
    const readiness = await this.waitForGlobalScopeReady();
    if (readiness === 'timeout') {
      // Lo scope globale non e' mai diventato ready: sessione inutilizzabile -> logout forzato.
      // Non si prosegue in nessun caso con l'enterProject.
      console.error('[REALTIME-V2] global scope never became ready, forcing logout');
      await this.authService.logout();
      return;
    }
    if (readiness !== 'ready') {
      // Disconnesso durante l'attesa: la riconnessione rifara' enterHome e riprovera'.
      this.projectScopeSyncRequested = true;
      return;
    }
    // Lo stato puo' essere cambiato durante l'attesa (switch progetto, runtime disabilitato, drop).
    if (
      !this.runtimeEnabled
      || !this.socket.connected$.value
      || this.desiredProjectId !== targetProjectId
    ) {
      this.projectScopeSyncRequested = true;
      return;
    }

    try {
      await this.socket.enterProject(targetProjectId, this.buildProjectCursors(targetProjectId));
      if (
        this.runtimeEnabled
        && this.socket.connected$.value
        && this.desiredProjectId === targetProjectId
      ) {
        this.enteredProjectId = targetProjectId;
        return;
      }

      await this.leaveAcceptedProject(targetProjectId);
      this.projectScopeSyncRequested = true;
    } catch (error) {
      console.warn('[REALTIME-V2] enterProject failed', error);
    }
  }

  /**
   * Attende che lo scope globale della sessione corrente sia ready. Esiti:
   * - 'ready': lo scope globale ha raggiunto lo stato ready (o lo era gia');
   * - 'disconnected': il socket si e' disconnesso durante l'attesa (il reconnect riprovera');
   * - 'timeout': lo scope globale non e' mai diventato ready entro il timeout (il chiamante fa logout).
   */
  private waitForGlobalScopeReady(): Promise<'ready' | 'timeout' | 'disconnected'> {
    if (this.entitySync.isGlobalScopeReady) {
      return Promise.resolve('ready');
    }

    return new Promise((resolve) => {
      const sub = new Subscription();
      let settled = false;
      const finish = (outcome: 'ready' | 'timeout' | 'disconnected') => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        sub.unsubscribe();
        resolve(outcome);
      };

      const timer = setTimeout(() => finish('timeout'), REALTIME_V2_GLOBAL_SCOPE_READY_TIMEOUT_MS);

      sub.add(
        this.entitySync.globalScopeReady$
          .pipe(filter((ready) => ready === true))
          .subscribe(() => finish('ready')),
      );
      sub.add(
        this.socket.connected$
          .pipe(filter((connected) => connected === false))
          .subscribe(() => finish('disconnected')),
      );
    });
  }

  private async leaveAcceptedProject(projectId: string): Promise<void> {
    if (!this.socket.connected$.value) {
      return;
    }

    try {
      await this.socket.leaveProject(projectId);
    } catch (error) {
      console.warn('[REALTIME-V2] leaveProject after stale enterProject failed', error);
    }
  }

  private buildGlobalCursors(): RealtimeCursor[] {
    return this.buildCursorsFromLastUpdates((key) => !key.startsWith('PRJ_'));
  }

  private buildProjectCursors(projectId: string): RealtimeCursor[] {
    const prefix = `PRJ_${projectId}_`;
    return this.buildCursorsFromLastUpdates((key) => key.startsWith(prefix), prefix);
  }

  private buildCursorsFromLastUpdates(
    includeKey: (key: string) => boolean,
    entityPrefixToStrip = '',
  ): RealtimeCursor[] {
    const lastUpdates = this.lastUpdateQuery.getValue() as LastUpdateState;
    return Object.entries(lastUpdates)
      .filter(([key, info]) => includeKey(key) && !!info?.lastId && !!info?.lastUpdate)
      .map(([key, info]) => {
        const storeName = entityPrefixToStrip ? key.substring(entityPrefixToStrip.length) : key;
        return {
          entity: toRealtimeV2EntityName(storeName),
          id: info.lastId,
          lastUpdated: info.lastUpdate,
        };
      });
  }
}
